import type { ExpressionVariables } from '@shared/helpers/expression-variables.helpers';
import type { ProjectMetadata } from '@shared/models/project.model';
import { EXPRESSION_VARIABLE_NAME as NAME } from '@shared/constants/expression-variables-names.constant';
import { BUILDING_LIFETIME_DEFAULT } from '@shared/helpers/project-factory.helpers';

export type LegacyProjectMetadataForExpression = ProjectMetadata & {
  building_lifetime?: number;
};

/**
 * Read legacy {@link ProjectMetadata.building_lifetime} when still present in persisted JSON.
 * @param meta Building metadata.
 * @returns Custom lifetime or undefined when unset.
 */
export const getLegacyBuildingLifetime = (
  meta: ProjectMetadata,
): number | undefined =>
  (meta as LegacyProjectMetadataForExpression).building_lifetime;

/**
 * Resolve building lifetime for UI and helpers (matches compute overlay behaviour).
 * @param meta Building metadata.
 * @returns Lifetime in years.
 */
export const getBuildingLifetimeFromMetadata = (
  meta: ProjectMetadata,
): number => getLegacyBuildingLifetime(meta) ?? BUILDING_LIFETIME_DEFAULT;

/**
 * Design-backed expression variables omit legacy fields still stored on building.meta.
 * @param meta Building metadata (may include pre-design fields).
 * @param parentVariables Variables from design or legacy metadata path.
 * @returns Parent variables with legacy metadata fields applied.
 */
export const withLegacyMetadataExpressionFields = (
  meta: ProjectMetadata,
  parentVariables: ExpressionVariables,
): ExpressionVariables => {
  const buildingLifetime = getLegacyBuildingLifetime(meta);
  if (buildingLifetime === undefined) return parentVariables;
  return {
    ...parentVariables,
    [NAME.BUILDING_LIFETIME]: buildingLifetime,
  };
};
